import Symbol from '@/components/elements/Symbol';
import HighlightMatch from '@/components/HighlightMatch';
import Icon from '@/components/icons/Icon';
import { classnames } from '@/helpers';
import { customTwMerge } from '@/helpers/theme';
import useMobileView from '@/hooks/useMobileView';
import { _s } from '@/locale';
import { ReactNode, RefObject } from 'react';
import { twJoin } from 'tailwind-merge';
import { KeywordSuggestion, KeywordSuggestionOption } from '../KeywordAutoSuggest/KeywordAutoSuggest.type';
import { LocationSuggestion, LocationSuggestionOption } from '../LocationAutoSuggest/LocationAutoSuggest.types';
import { AutoSuggestionSection } from './types';

export const AutoSuggestInputComponent = ({
  inputProps,
  onClear,
  leftSlot = null,
  className = '',
  isFocused,
  hideClearInput = false,
  inputRef,
}: {
  inputProps: any;
  onClear: any;
  leftSlot: ReactNode;
  className?: string;
  isFocused?: boolean;
  hideClearInput?: boolean;
  inputRef?: RefObject<HTMLInputElement | null>;
}) => {
  const { ref, title, onFocus, onBlur, ...rest } = inputProps;
  const hasInputValue = Boolean(inputProps?.value?.length);

  return (
    <div onFocus={inputProps.onFocus} onBlur={inputProps.onBlur} className="outline-none">
      <div
        className={customTwMerge('space-x-sm px-sm relative flex items-center border', className)}
        onClick={() => inputRef?.current?.focus()}
        onMouseDown={(e) => isFocused && e.preventDefault()}>
        {leftSlot && <div className="z-10 min-w-[24px] flex-shrink-0">{leftSlot}</div>}
        <div className="py-xs z-10 flex w-full flex-col">
          {isFocused && <span className="text-fg_secondary text-xs">{title}</span>}
          <input
            ref={ref}
            {...rest}
            className={twJoin(
              'w-full text-ellipsis bg-transparent',
              hasInputValue && 'pr-lg',
              !hasInputValue && !isFocused && 'placeholder-fg_primary',
            )}
          />
        </div>

        {hasInputValue && !hideClearInput && (
          <button
            className={classnames(
              'bg-bg_secondary right-sm invisible absolute top-1/2 z-10 flex h-[32px] w-[32px] -translate-y-1/2 items-center justify-center rounded-full group-focus-within:visible group-hover:visible',
            )}
            onClick={onClear}
            aria-label="clear">
            <Icon variant="close-alt" />
          </button>
        )}
      </div>
      {/* White background when input is focused  */}
      <div
        className={classnames(
          '-z-1 right-xxs pointer-events-none absolute inset-[1px] rounded-full',
          isFocused && '!bg-bg_neutral_alwaysLight drop-shadow-xs',
        )}></div>
    </div>
  );
};

export const KeywordSuggestionItem = ({
  suggestion,
  options,
  onRemoveHistorySuggestion,
}: {
  suggestion: KeywordSuggestion;
  options: KeywordSuggestionOption;
  onRemoveHistorySuggestion?: (suggestion: KeywordSuggestion) => void;
}) => {
  const handleOnRemoveSuggestion = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onRemoveHistorySuggestion?.(suggestion);
  };

  switch (suggestion.type) {
    case 'spell':
      return (
        <span className="spelling-suggestion">
          <em>{suggestion.text.replace(/ /g, '\u00a0')}</em>
        </span>
      );
    case 'place':
    case 'brand_deal':
      const imageSrc = suggestion.type === 'brand_deal' ? suggestion.logoImageUrl : suggestion.profileImage;
      return (
        <span className="place-suggestion space-x-md py-md flex items-center">
          <img
            className="place-image"
            alt=""
            width={24}
            height={24}
            src={imageSrc ? imageSrc : 'https://www.bokadirekt.se/favicon.ico'}
          />
          <span className="place-label text-fg_secondary">
            <HighlightMatch
              query={options.query}
              text={suggestion.text}
              options={{ className: 'text-bold text-fg_alwaysDark' }}
            />
          </span>
        </span>
      );
    case 'history':
      return (
        <div className="space-x-md py-xs flex items-center rounded-md">
          <Symbol classNames={'rounded-sm'}>
            <Icon variant="search" size="sm" />
          </Symbol>
          <span className="text-fg_secondary flex-1">{suggestion.text}</span>
          <button
            id="remove_suggested"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={handleOnRemoveSuggestion}
            className="flex-shrink-0 px-4">
            <div className="pointer-events-none block">
              <Icon variant="close-circle" />
            </div>
          </button>
        </div>
      );
    case 'popular':
      return (
        <span className={classnames('history-suggestion py-xs space-x-md flex items-center rounded-md')}>
          <Symbol classNames={'rounded-sm'}>
            <Icon variant="search" size="sm" />
          </Symbol>
          <span className="text-fg_secondary">{suggestion.text}</span>
        </span>
      );
    case 'category':
      return (
        <span className={classnames('history-suggestion py-xs space-x-md flex items-center rounded-md')}>
          <Symbol classNames={'rounded-sm'}>
            <Icon variant="search" size="sm" />
          </Symbol>
          <span className="text-fg_secondary">
            <HighlightMatch
              query={options.query}
              text={suggestion.text}
              options={{ className: 'text-bold text-fg_alwaysDark' }}
            />
          </span>
        </span>
      );
  }
};

export const LocationSuggestionItem = ({
  suggestion,
  options,
  onRemoveHistorySuggestion,
}: {
  suggestion: LocationSuggestion;
  options: LocationSuggestionOption;
  onRemoveHistorySuggestion: (suggestion: LocationSuggestion) => void;
}) => {
  const handleOnRemoveSuggestion = (e) => {
    onRemoveHistorySuggestion(suggestion);
    e.preventDefault();
  };
  return (
    <div className="space-x-md py-xs flex items-center rounded-md">
      <Symbol classNames={'rounded-sm'}>
        {suggestion.type === 'geolocation' ? (
          <Icon variant="user-location" size="sm" color="fg_info_bold" />
        ) : (
          <Icon variant="location" size="sm" />
        )}
      </Symbol>
      <span className={classnames(suggestion.type, 'text-fg_secondary flex-1')}>
        <HighlightMatch
          query={options.query}
          text={suggestion.label}
          options={{ className: 'text-bold text-fg_alwaysDark' }}
        />
      </span>
      {suggestion.type === 'history' && (
        <button id="remove_suggested" onClick={handleOnRemoveSuggestion} className="flex-shrink-0 px-4">
          <div className="pointer-events-none block">
            <Icon variant="close-circle" />
          </div>
        </button>
      )}
    </div>
  );
};

export const KeywordAutoSuggestSectionTitle = (section: AutoSuggestionSection<KeywordSuggestion>) => {
  const { isMobileView } = useMobileView();
  return section.title ? (
    <div className={classnames(isMobileView ? 'text-l text-fg_secondary mb-sm' : 'mb-md text-h-s text-fg_primary')}>
      {_s(section.title)}
    </div>
  ) : null;
};

export const LocationAutoSuggestSectionTitle = (section: AutoSuggestionSection<LocationSuggestion>) => {
  return section.title ? (
    <div className="text-black-600 text-m py-3 pl-3 font-semibold uppercase">{_s(section.title)}</div>
  ) : null;
};
